import type { App } from 'vue';
import { Toast } from '@cfx/base-mobile';
import { createAdId, createMaterialUrl } from '@markting/mk-create-h5-url';
import { loadMountDocument, WxSDK } from '@cfx/icorki-jwx';
import wxJssdk from '@cfx/wx-jssdk';
import { getUser } from '@dustess/itimo-core';
import MkToast from '@components/mk-toast';
import { reqSaleShareRecordPost } from '@/model/commonApi';
import { reportSendTimes, reportReach } from '@/hooks/useReport';
import { spmConfig } from '@/spm';
import util from '@utils/index';
import { getHashQuery } from '@/utils/common';
import { showAuthMask } from '@/utils/auth';

export default {
  install(app: App<Element>) {
    app.config.globalProperties.util = util;

    // 获取授权信息
    app.config.globalProperties.$getAuthUserInfo = () => util.storage.getItem('c_user', localStorage) || {};

    // 跳转页面
    app.config.globalProperties.$toLink = async (url: string) => {
      if (!url) return;
      const { $isMobile, $isMp, $materialId } = app.config.globalProperties;

      // 如果跳转页面素材id相同则禁止跳转
      if (url.includes($materialId)) return;

      // 如果小程序中点击跳转任意链接则禁止跳转
      if ($isMp && url.includes('/mk-h5-content/#/link')) {
        MkToast({ tip: '小程序暂不支持跳转' });
        return;
      }
      await app.config.globalProperties.$leavePageFun();
      if ($isMobile || $isMp) window.location.href = url;

      else window.open(url, '_blank');
    };

    // 设置页面回显功能
    app.config.globalProperties.$handleSaveHistory = (type = 'set', val?: number) => {
      const { $materialId } = app.config.globalProperties;
      if (type === 'set') {
        window.localStorage.setItem('saveHistory', `${$materialId}$$${val}`);
      }
      else {
        const reSaveHistory = window.localStorage.getItem('saveHistory') || '';
        window.localStorage.removeItem('saveHistory');
        if (reSaveHistory) {
          const [id, reVal] = reSaveHistory.split('$$');
          if (id === $materialId) return Number(reVal);
        }
      }
      return 0;
    };

    // 去绑定手机号
    app.config.globalProperties.$toBindMobile = async (signUrl = '//res.wx.qq.com/open/js/jweixin-1.3.2.js') => {
      const { targetId, nickname = '微信用户' } = app.config.globalProperties.$getAuthUserInfo();
      (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm15, `content=${JSON.stringify({ targetId, nickname })}`);
      if (!nickname && app.config.globalProperties.$materialType !== 'form') {
        Toast('跳转失败, 请退出重试！');
        return;
      }
      try {
        await loadMountDocument([signUrl]);
        window.wx.miniProgram.postMessage({ data: { unionid: targetId, nickname, isNotBindPhone: true } });
        window.wx.miniProgram.redirectTo({ url: '/pages/material-details/get-phone-number' });
        (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm16);
      }
      catch (err) {
        console.error(err);
        (window as any).Monitor?.sendSPM(spmConfig.bindMobile.spm17);
      }
    };

    // 处理关联内容的链接生成
    app.config.globalProperties.$handelRelationUrl = async (type: string, id: string) => {
      const { $env, $urlObj, $isMp, $uuid } = app.config.globalProperties;
      // 当关联类型为 商品时，需要跳转到crm那边去
      if (type === 'good') {
        const { VUE_APP_M_C_BASE_URL } = $env;
        const linkUrl = `${VUE_APP_M_C_BASE_URL}/scrm-goods/#/goodsDetail?id=${id}&account=${$urlObj.account}&type=share`;
        return linkUrl;
      }
      // 当为移动端时，表明是在C端访问，所以需要重新处理 user 数据，然后传入进去
      // 当为预览时，表明包含了iframe查看，则也需要重新处理 user 数据，然后传入进去
      const userInfo: any = {
        id: $urlObj.userid,
        account: $urlObj.account
      };

      const { project } = $urlObj;

      if (project === 'qw') {
        userInfo.corpId = $urlObj.corpId;
        userInfo.uid = $urlObj.qwUid;
        userInfo.qwUserId = $urlObj.qwUserId;
        userInfo.agentId = $urlObj.agentId;
        userInfo.suiteId = $urlObj.suiteId;
      }

      let otherParams: any = { client: 'c-client' };

      otherParams.projectSecondScene = $urlObj.projectSecondScene;

      if ($isMp) {
        // 当为小程序时，要跳转到其他C端时，需要重新处理otherParams的值，目的：在小程序里面跳转时，保持链接参数统一（operateEntity、parentActivityId等等）
        const { authPhone, closeWxAuth, showShare, sourcePlatform, isWxMp, isQwMp, operateEntity, parentActivityId, secondScene, shareId, nickName, unionId, saleShareId, showGoBackBtn } = $urlObj;
        otherParams = {
          ...otherParams,
          activityId: $uuid(),
          closeWxAuth,
          isWxMp,
          isQwMp,
          sourcePlatform,
          operateEntity,
          parentActivityId,
          secondScene,
          shareId,
          saleShareId,
          unionId,
          nickName,
          showGoBackBtn,
          showShare,
          authPhone,
          toHref: 'yes'
        };
      }

      const deliverType = $urlObj?.deliverType || 'staffShare';
      const secondScene = $urlObj?.saleShare === 'false' ? '1' : '0';

      // sceneType为conver-params时，不需要传入parentAdId
      const parentAdIdObj: any = {};
      if ($urlObj?.sceneType !== 'conver-params' && $urlObj?.parentAdId) {
        Object.assign(parentAdIdObj, { parentAdId: $urlObj.parentAdId });
      }

      Object.assign(otherParams, {
        isMini: $isMp,
        noCorpIdInUrl: $isMp,
        deliverType,
        ...parentAdIdObj,
        shareId: $urlObj?.shareId,
        sourceFrom: $urlObj?.sourceFrom,
        shareType: $urlObj?.shareType || (secondScene === '1' ? 'wxUser' : 'staff'),
        sourceId: deliverType !== 'staffShare' ? $urlObj?.sourceId : undefined
      });

      const { linkUrl } = await createMaterialUrl({ id }, type, 'qw', true, otherParams, userInfo, $urlObj?.sceneType);
      return linkUrl;
    };

    const { config: { globalProperties: { $urlObj, $materialType, $materialId, $operateEntity, $isWx, $isQw, $uuid, $getAuthUserInfo, $activityId } } } = app;

    // 生成h5的分享地址，用于“分享的”（移动端App、企微、微信都可以使用）
    const createH5ShareLink = async (linkParams: any = {}) => {
      // 新的参数对象
      const newUrlObj = JSON.parse(JSON.stringify({ ...$urlObj, ...linkParams }));
      // 要删除的参数对象
      const deleteParams = {
        // 所有场景下要删除的参数
        commom: [
          'code',
          'appId',
          'state',
          'activityId',
          'editLinkMe',
          'thirdAuthTip',
          'showCompleteTaskBtn',
          'admin',
          'parentActivityId',
          'showShare',
          'toHref',
          'isSideMenu',
          'showGoBackBtn',
          'timer',
          'adId',
          'view'
        ],
        // 企微浏览器场景下要删除的参数
        qw: [],
        // 微信浏览器场景下要删除的参数
        wx: [],
        // 移动端App场景下要删除的参数
        dustessApp: [
          'dustessAppId',
          'dustessPackage',
          'view',
          'hideLinkMe'
        ]
      };

      // 当前浏览器环境
      let currEnvironmental: keyof typeof deleteParams = 'commom';

      if ($isWx) currEnvironmental = 'wx';
      else if ($isQw) currEnvironmental = 'qw';

      // 当前浏览器环境要删除的参数
      const currEnvironmentalDeleteParams = deleteParams[currEnvironmental];

      // 最终要删除的参数
      const finalDeleteParams = [
        ...deleteParams.commom,
        ...currEnvironmentalDeleteParams
      ];

      // 删除newUrlObj的参数
      finalDeleteParams.forEach((key) => {
        delete newUrlObj[key];
      });

      // 获取origin、pathname
      const { origin, pathname, hash } = window.location;
      let link = origin + pathname + hash.split('?')[0];

      // sceneType为conver-params时，不需要传入parentAdId
      const parentAdIdObj: any = {};
      if ($urlObj?.sceneType !== 'conver-params') {
        Object.assign(parentAdIdObj, { parentAdId: $urlObj?.adId });
      }

      // 转发时需要重新生成adId
      const params:any = {
        account: $urlObj.account,
        extraData: {
          ...newUrlObj,
          ...parentAdIdObj,
          materialId: $materialId,
          materialType: $materialType,
          deliverType: 'customerShare', // 客户分享
          sourceDeliverType: $urlObj.sourceDeliverType || $urlObj.deliverType || 'staffShare', // 一级来源
          shareId: linkParams?.shareId,
          shareType: $urlObj.view === 'yes' ? 'staff' : linkParams?.shareType,
          sourceId: $urlObj?.sourceId
        },
        activityId: $activityId,
        sceneType: $urlObj.view === 'yes' ? 'sidebar-H5' : 'reshare-H5'
      };

      // deliverType 根据 shareType 判断
      if (params.extraData.shareType === 'staff') params.extraData.deliverType = 'staffShare';

      const { success, data } = await createAdId(params);

      if (success) {
        // 生成分享链接
        link += `?adId=${data?.adId}&p_id=${data?.p_id}`;
        return link;
      }

      return '';
    };

    // 设置sdk
    let sdkInstance: any = null;
    app.config.globalProperties.$wxSdk = () => {
      if (sdkInstance) return sdkInstance;
      sdkInstance = new WxSDK({
        config: {
          corpId: $urlObj.corpId,
          agentId: $urlObj.agentId,
          debug: false
        },
        account: $urlObj.account,
        jsApiList: ['hideAllNonBaseMenuItem', 'showAllNonBaseMenuItem']
      });

      return sdkInstance;
    };

    // 企微-销售上报事件
    const qwReportSDKFun = async (shareId: string, shareType: string) => {
      /**
       * !2020-04-21 15:24 企微1.0.3需求，将这块的上报进行更改
       *   1、上报地址更改
       *   2、上报参数更改
       *   3、完全不用兼容以前的情况，直接改
       */
      const parmas = {
        companyId: $urlObj.account,
        operateEntity: $operateEntity,
        operateEntityObj: {
          operateEntityId: $materialId,
          operateEntityType: $materialType
        },
        saleShareId: shareId,
        shareType,
        // taskId 任务id 通过营销计划采集上来的行为，需要此参数-2021-05-19 新增
        taskId: $urlObj.taskId || '',
        userId: $urlObj.qwUid
      };
      await reqSaleShareRecordPost(parmas);
    };

    // 设置sdk
    app.config.globalProperties.$initMkWxSdk = () => {
      // 微信授权回调时，在地址栏上的参数，目前是放在sessionStorage里面的，所以要通过sessionStorage获取
      const authCallbackUrlParams = util.storage.getItem('authCallbackUrlParams') || null;
      const appId = authCallbackUrlParams?.appId;

      const initMkWxSdk = wxJssdk.initJSSDK({
        account: $urlObj.account,
        autoGetCurExternal: true, // 默认请求外部联系人id
        // 个微需要的参数
        appId,
        // 企微需要的参数
        corpId: $urlObj.corpId,
        agentId: $urlObj.agentId,
        registerJsApiList: ['hideAllNonBaseMenuItem', 'showAllNonBaseMenuItem']
      });

      return initMkWxSdk;
    };

    // 设置分享
    const initShare = async (shareData: any = {}, callBack: (res: any) => void) => {
      const initMkWxSdk = await app.config.globalProperties.$initMkWxSdk();
      await initMkWxSdk.initShare(shareData, { callBack });
    };

    // 执行微信分享操作
    app.config.globalProperties.doWxShare = async ({ link, title, desc, cover }: { [key: string]: string | number }, linkParams: { [key: string]: any } = {}) => {
      // 判断是否需要授权，showMask为true时，表示需要授权但是没有授权，不执行分享操作
      const showMask = await showAuthMask();
      if (showMask) return;

      const { scene } = $urlObj;
      // 生成分享id，用于拼接在分享链接和分享成功回调上报
      const shareId = $uuid();
      linkParams.channel = 'friend';
      const userInfo = $getAuthUserInfo();
      // 如果存在targetId
      if (userInfo?.targetId) {
        linkParams.currentShareId = shareId;
        linkParams.shareType = 'wxUser';
        linkParams.shareId = userInfo?.targetId;
      }
      const qwUser = getUser();
      // 判断是否员工
      if (qwUser?.uid) {
        linkParams.qwUid = qwUser?.uid;
        linkParams.qwUserId = qwUser?.qwUserId;
        linkParams.shareType = 'staff';
        linkParams.shareId = qwUser?.uid;
        linkParams.saleShareId = shareId;
      }

      // 如果传递了link，就不再生成
      let targetUrl: any = '';
      if (link) {
        targetUrl = link;
      }
      else {
        // 生成h5的分享地址
        targetUrl = await createH5ShareLink(linkParams);
      }

      const reportFn = (type: any) => {
        // 触达上报，先截取url上携带的adId
        const queryParams = getHashQuery(targetUrl?.split('?')?.[1]);
        if (queryParams?.adId) {
          const targetReportParams = {
            account: $urlObj?.account, // 公司id
            adId: queryParams?.adId // 投放id
          };
          reportReach(targetReportParams);
        }
        if ($isQw) {
          qwReportSDKFun(shareId, type);
          reportSendTimes(app.config.globalProperties.$materialId, app.config.globalProperties.$materialType);
        }
        else {
          app.config.globalProperties.$report('mkFullLinkShare', { currentShareId: shareId, channel: type });
        }
      };

      if ($isWx) {
        const wxSdk = await app.config.globalProperties.$wxSdk();
        wxSdk.setShareConfig({ link: targetUrl, title, desc: desc || '点击查看', imgUrl: cover }, ({ success, type }: any) => {
          if (!success) return;
          // 有场景值才上报
          scene && reportFn(type);
        });
      }
      if ($isQw) {
        await initShare({ link: targetUrl, title, desc: desc || '点击查看', imgUrl: cover }, (res) => {
          if (res.success) {
            // 有场景值才上报
            scene && reportFn(res.type);
          }
        });
      }
    };
  }
};
